import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { useAppDispatch } from 'store/hooks';
import flag from 'node-env-flag';
import { Product } from '@portal/portal-core';
import { FjSideNav, FjSideNavItem } from 'components/primitives';
import Invites from 'icons/invites';
import InvitesFlowjoIcon from 'icons/invites-flowjo';
import InvitesSeqgeqIcon from 'icons/invites-seqgeq';
import ReportsIcon from 'icons/reports';
import SettingsIcon from 'icons/settings';
import UsersIcon from 'icons/users';
import SharedComputerIcon from 'icons/shared-computers';
import CustomFieldsIcon from 'icons/custom-fields';
import LicenseDetailsIcon from 'icons/license-details';
import { currentSharedComputerMaxAllowedSelector } from 'selectors';
import { TOOL_BUTTON_NAMES } from 'constants/cloud-admin';
import { CloudAdminActions } from 'actions';
import { selectActiveTool } from 'selectors/cloud-admin';
import { selectActiveTab } from 'selectors/product-tab';

const { NAMES_UI_TABS } = Product;

const AdminToolArea = (): React.ReactElement => {
  const selectedTool = useSelector(selectActiveTool);
  const selectedProductTab = useSelector(selectActiveTab);
  const rawSharedComputerMax = useSelector(currentSharedComputerMaxAllowedSelector);
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const selectTool = (name: string): void => {
    dispatch(CloudAdminActions.selectedToolButton(name));
  };
  const clearStatusFilter = (): void => {
    dispatch(CloudAdminActions.clearFilterByStatus());
  };

  // Replaces the class shouldComponentUpdate "hack: cover up sharedComputerMax
  // returning null for a few renders after update". The class suppressed the
  // re-render (keeping the previous non-null value) only while the product tab
  // AND selected tool were unchanged; here we keep the last non-null value under
  // the same condition and otherwise fall through to the raw value.
  const stableMax = useRef(rawSharedComputerMax);
  const maskKey = useRef({ tab: selectedProductTab, tool: selectedTool });
  if (maskKey.current.tab !== selectedProductTab || maskKey.current.tool !== selectedTool) {
    maskKey.current = { tab: selectedProductTab, tool: selectedTool };
    stableMax.current = rawSharedComputerMax;
  } else if (rawSharedComputerMax != null) {
    stableMax.current = rawSharedComputerMax;
  }
  const sharedComputerMax = stableMax.current;

  // constructor + componentDidUpdate facs-tab tool sync
  const mounted = useRef(false);
  const prevTab = useRef(selectedProductTab);
  useEffect(() => {
    const prev = prevTab.current;
    if (!mounted.current) {
      mounted.current = true;
      if (selectedProductTab === NAMES_UI_TABS.facsWorkflowManager) {
        selectTool(TOOL_BUTTON_NAMES.FACS_WORKFLOW_MANAGER_LICENSES);
      }
    } else if (
      selectedProductTab === NAMES_UI_TABS.facsWorkflowManager &&
      prev !== NAMES_UI_TABS.facsWorkflowManager
    ) {
      selectTool(TOOL_BUTTON_NAMES.FACS_WORKFLOW_MANAGER_LICENSES);
    } else if (
      selectedProductTab !== NAMES_UI_TABS.facsWorkflowManager &&
      prev === NAMES_UI_TABS.facsWorkflowManager
    ) {
      selectTool(TOOL_BUTTON_NAMES.USERS);
    }
    prevTab.current = selectedProductTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductTab]);

  const handleTabChange = (tabId: string) => {
    selectTool(tabId);
    clearStatusFilter();
  };

  const getInvitesIcon = () => {
    switch (selectedProductTab) {
      case NAMES_UI_TABS.flowJo:
        return InvitesFlowjoIcon;
      case NAMES_UI_TABS.seqGeq:
        return InvitesSeqgeqIcon;
      default:
        return Invites;
    }
  };

  // FjSideNav consumes `style` as a custom side-nav theme; the app DefaultTheme
  // supplies `colors` at runtime via the surrounding ThemeProvider, so cast to
  // the expected prop shape rather than duplicating that value here.
  const sideNavStyle = {
    primaryColor: theme.colors.selected,
    backgroundColor: theme.colors.toolbar,
    width: theme.grid.units(4),
    titleFontSize: theme.fontSizes.sidebarTitle.default,
    titleHeight: '70px',
    iconBoxHeight: theme.grid.units(4),
    iconSize: '40px',
  } as React.ComponentProps<typeof FjSideNav>['style'];

  const InvitesIcon = getInvitesIcon();

  switch (selectedProductTab) {
    case NAMES_UI_TABS.bdrc:
      return (
        <FjSideNav
          activeTab={selectedTool}
          style={sideNavStyle}
          onTabSelected={(id: string) => handleTabChange(id)}
        />
      );
    case NAMES_UI_TABS.facsWorkflowManager:
      return (
        <FjSideNav
          activeTab={selectedTool}
          style={sideNavStyle}
          onTabSelected={(id: string) => handleTabChange(id)}
        >
          <FjSideNavItem
            title="Licenses"
            id={TOOL_BUTTON_NAMES.FACS_WORKFLOW_MANAGER_LICENSES}
            icon={<LicenseDetailsIcon style={theme.colors.sideNavIcons} />}
            data-testid="facs-workflow-manager-license-tool-button"
          />
          <></>
        </FjSideNav>
      );
    default:
      return (
        <FjSideNav
          activeTab={selectedTool}
          style={sideNavStyle}
          onTabSelected={(id: string) => handleTabChange(id)}
        >
          <FjSideNavItem
            title="Users"
            id={TOOL_BUTTON_NAMES.USERS}
            icon={<UsersIcon style={theme.colors.sideNavIcons} />}
            titleFontSizeOverride={theme.fontSizes.sidebarTitle.large}
            data-testid="users-tool-button"
          />
          <FjSideNavItem
            title="Invites"
            id={TOOL_BUTTON_NAMES.INVITES}
            icon={<InvitesIcon style={theme.colors.sideNavIcons} />}
            data-testid="invites-tool-button"
          />
          {flag(process.env.FF__SUPERADMIN_CUSTOM_FIELDS, true) ? (
            <FjSideNavItem
              title="Custom Fields"
              id={TOOL_BUTTON_NAMES.CUSTOM_FIELDS}
              icon={<CustomFieldsIcon style={theme.colors.sideNavIcons} />}
              data-testid="custom-fields-tool-button"
            />
          ) : (
            <></>
          )}
          <FjSideNavItem
            title="Reports"
            id={TOOL_BUTTON_NAMES.REPORTS}
            icon={<ReportsIcon style={theme.colors.sideNavIcons} />}
            data-testid="reports-tool-button"
          />
          <FjSideNavItem
            title="Settings"
            id={TOOL_BUTTON_NAMES.SETTINGS}
            icon={<SettingsIcon style={theme.colors.sideNavIcons} />}
            data-testid="settings-tool-button"
          />
          {sharedComputerMax ? (
            // Only render shared computers button if the license is allotted shared computers.
            <FjSideNavItem
              title="Shared"
              id={TOOL_BUTTON_NAMES.SHARED}
              icon={<SharedComputerIcon style={theme.colors.sideNavIcons} />}
              data-testid="shared-computers-tool-button"
            />
          ) : (
            <></>
          )}
        </FjSideNav>
      );
  }
};

export default AdminToolArea;
